import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

/**
 * Subscription state surfaced to the gate. Backend returns either the live
 * SnakeCaseSubscription shape (`status`, `end_date`, `plan_slug`, ...) or
 * the BFF placeholder for tenants with no active subscription
 * (`status: 'none'`, `renewal_date`, `plan_slug: null`).
 *
 * We normalise to the few fields the UI actually needs, and expose a single
 * boolean `active` so the gate's check stays trivial.
 */
export interface SubscriptionStatus {
  active: boolean;
  status: string; // 'active' | 'trial' | 'past_due' | 'cancelled' | 'suspended' | 'none' | ...
  expiresAt: string | null;
  planSlug: string | null;
}

/** Statuses that grant full platform access. `past_due` and `unpaid`
 *  are the dunning-grace window: payment failed, retries in flight, but
 *  the team must still operate. `suspended`/`cancelled` (post D+30 / D+90)
 *  route operational users to SubscriptionExpiredPage. */
const ACTIVE_STATUSES = new Set([
  'active',
  'trial',
  'trialing',
  'past_due',
  'unpaid',
]);

/**
 * O que `GET /billing/subscription` de fato serve. Todas as chaves são
 * opcionais e as DUAS grafias estão declaradas: o BFF pode normalizar para
 * camelCase ou repassar o snake_case do janus-billing verbatim, e cada
 * leitura abaixo mantém o `??` de sempre.
 */
interface RawSubscriptionPayload {
  status?: string;
  subscription_status?: string;
  end_date?: string | null;
  endDate?: string | null;
  expires_at?: string | null;
  expiresAt?: string | null;
  renewal_date?: string | null;
  renewalDate?: string | null;
  plan_slug?: string | null;
  planSlug?: string | null;
}

export function useSubscriptionStatus() {
  const { user } = useAuth();

  return useQuery<SubscriptionStatus>({
    queryKey: ['subscription', user?.tenantId],
    queryFn: async () => {
      // Tenant ID is derived server-side from the JWT — the URL deliberately
      // takes no tenantId so callers can never IDOR across tenants.
      const { data } = await api.get<RawSubscriptionPayload>('/billing/subscription');

      const status = String(data?.status ?? data?.subscription_status ?? 'unknown');
      const expiresAt =
        data?.end_date ??
        data?.endDate ??
        data?.expires_at ??
        data?.expiresAt ??
        data?.renewal_date ??
        data?.renewalDate ??
        null;
      const planSlug = data?.plan_slug ?? data?.planSlug ?? null;

      return {
        active: ACTIVE_STATUSES.has(status),
        status,
        expiresAt,
        planSlug,
      };
    },
    enabled: !!user?.tenantId,
    // Billing data is slow-changing; 5 min stale is plenty and avoids a
    // refetch on every page mount.
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}
